import {useQuery} from '@tanstack/react-query';
import {useCallback, useId, useMemo, useState} from 'react';
import {useLocale} from 'state/locale';
import {Option} from 'types';
import {request} from 'utils/request-axios';

import {MultiChoice, OptionsLocal, OptionsServer, SingleChoice} from '../type';

type UseOptionsProps = {
    isMultiChoices?: boolean;
    isSearchable?: boolean;
    isShown: boolean;
    searchQuery: string;
    searchQueryDebounced: string;
} & (MultiChoice | SingleChoice) &
    (OptionsLocal | OptionsServer);

type Value = UseOptionsProps['value'];

const isHidden = (options: Option): boolean => !!options.hidden;

const useOptions = ({
    isSearchable,
    isShown,
    searchQuery,
    searchQueryDebounced,
    ...props
}: UseOptionsProps) => {
    const id = useId();
    const locale = useLocale();

    const [cachedOptions, setCachedOptions] = useState<Option[]>(() => {
        if (props.serverRequest) {
            return props.serverRequest.initialSelectedOptions || [];
        }

        return props.options || [];
    });

    const enabled = useMemo((): boolean => {
        if (!props.serverRequest || !isShown) {
            return false;
        }

        if (isSearchable) {
            return searchQueryDebounced !== '';
        }

        return true;
    }, [isSearchable, isShown, props.serverRequest, searchQueryDebounced]);

    const query = useQuery<Option[]>({
        enabled,
        queryFn: async () => {
            // Only for server-side options
            if (!props.serverRequest) {
                return [];
            }

            const {fetcher, parser} = props.serverRequest;

            const data = await request({
                locale,
                method: 'GET',
                url: fetcher(searchQueryDebounced),
            });

            return parser ? parser(data) : data;
        },
        queryKey: [id, searchQueryDebounced],
        refetchOnWindowFocus: false,
        retry: 2,
    });

    const isLoading = enabled && (query.isLoading || query.isRefetching);

    const isOptionSelected = useCallback(
        (option: Option, value: Value = props.value): boolean => {
            if (props.isMultiChoices) {
                return value?.includes(option.value) || false;
            }

            return option.value === value;
        },
        [props.isMultiChoices, props.value],
    );

    const selectedOptions = useMemo(
        () => cachedOptions.filter((option) => isOptionSelected(option)),
        [cachedOptions, isOptionSelected],
    );

    const _options = useMemo((): Option[] => {
        // Server-side options
        if (props.serverRequest) {
            if (isSearchable && searchQueryDebounced === '') {
                return selectedOptions;
            }

            return query.data || [];
        }

        // Local options
        if (searchQuery === '') {
            return props.options || [];
        }

        return (props.options || []).filter(
            (option) =>
                typeof option.label === 'string' &&
                option.label.toLowerCase().includes(searchQuery.toLowerCase()),
        );
    }, [
        isSearchable,
        props.options,
        props.serverRequest,
        query.data,
        searchQuery,
        searchQueryDebounced,
        selectedOptions,
    ]);
    const options = _options.filter((option) => !isHidden(option));

    const saveAndSortCachedOptions = useCallback(
        (newValue: Value) =>
            setCachedOptions([
                // Selected Options
                ...options.filter((option) =>
                    isOptionSelected(option, newValue),
                ),
                // Unselected Options
                ...options.filter(
                    (option) => !isOptionSelected(option, newValue),
                ),
            ]),
        [isOptionSelected, options],
    );

    return {
        isLoading,
        isOptionSelected,
        options,
        refetch: query.refetch,
        saveAndSortCachedOptions,
        selectedOptions,
    };
};

export default useOptions;
